import { ApiFetch } from '../../../shared/utils/fetch';

/**
 * Reviews & testimonials moderation (PE-2171), against osprey-api's
 * products.controller.ts (`/reviews`) and testimonials.controller.ts (`/testimonials`).
 *
 * Both are tenant-wide (x-tenant-id, via ApiFetch — see shared/utils/fetch.ts) and share
 * an identical four-route shape: list / set status / set featured / delete. There is
 * deliberately NO default status filter server-side — omitting `status` returns every
 * curation state, which is what the "All" pill relies on.
 */

export type CurationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/** No product name on the row (verified against the controller) — callers resolve it. */
export interface Review {
  id: string;
  productId: string;
  customerId: string;
  customerName: string;
  rating: number;
  title?: string;
  body?: string;
  status: CurationStatus;
  isFeatured: boolean;
  verifiedPurchase: boolean;
  createdAt: string;
}

export interface Testimonial {
  id: string;
  customerName: string;
  customerRole?: string;
  rating?: number;
  title?: string;
  body: string;
  productId?: string;
  status: CurationStatus;
  isFeatured: boolean;
  createdAt: string;
}

export interface ReviewListParams {
  status?: CurationStatus;
  isFeatured?: boolean;
  page?: number;
  limit?: number;
}

export interface TestimonialListParams extends ReviewListParams {
  productId?: string;
}

const buildQuery = (params?: ReviewListParams | TestimonialListParams): string => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, String(value));
    });
  }
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const reviewsApi = {
  list: (params?: ReviewListParams): Promise<Paginated<Review>> =>
    ApiFetch.get<Paginated<Review>>(`/reviews${buildQuery(params)}`),

  // Wire method is PATCH — ApiFetch.put issues PATCH (see shared/utils/fetch.ts).
  setStatus: (id: string, status: CurationStatus): Promise<Review> =>
    ApiFetch.put<Review>(`/reviews/${id}/status`, { status }),

  // 409 `Only APPROVED reviews can be featured` unless the review is already APPROVED;
  // moving a review out of APPROVED auto-unfeatures it server-side.
  setFeatured: (id: string, isFeatured: boolean): Promise<Review> =>
    ApiFetch.put<Review>(`/reviews/${id}/featured`, { isFeatured }),

  remove: (id: string): Promise<void> => ApiFetch.del<void>(`/reviews/${id}`),
};

export const testimonialsApi = {
  list: (params?: TestimonialListParams): Promise<Paginated<Testimonial>> =>
    ApiFetch.get<Paginated<Testimonial>>(`/testimonials${buildQuery(params)}`),

  setStatus: (id: string, status: CurationStatus): Promise<Testimonial> =>
    ApiFetch.put<Testimonial>(`/testimonials/${id}/status`, { status }),

  setFeatured: (id: string, isFeatured: boolean): Promise<Testimonial> =>
    ApiFetch.put<Testimonial>(`/testimonials/${id}/featured`, { isFeatured }),

  remove: (id: string): Promise<void> => ApiFetch.del<void>(`/testimonials/${id}`),
};
